import { defineStore } from "pinia"

import { LocationsState } from "~/js/interfaces/locations"
import { Facet, Filter, SingleFilter } from "~/js/interfaces/typesense"

export const useLocationsState = defineStore("locations", {
    state: (): LocationsState => ({
        facets: [],
        filters: {
            searchQuery: "",
            filterQuery: `site:${window.language}`,
            countries: {
                field: "countries",
                values: [1,2,3,4],
            },
            statusses: {
                field: "statusses",
                values: [1,3],
            }
        },
        index: `montea_${window.typesense_environment}_warehouses`,
        loading: true,
        pagination: {
            current: 0,
            totalPages: 0,
            totalResults: 0,
            foundResults: 0,
            itemsPerPage: 160,
        },
        settings: {
            showFilters: true,
            useQueryParams: true,
        },
        sorting: {
            type: "post_date_timestamp",
            order: "desc",
        },
        locations: null,
    }),

    persist: {
        pick: [
            "filters.countries",
            "filters.statusses",
        ],
    },

    actions: {
        /**
         * Rehydrate the store from the server-rendered seed (BC-32) — the
         * warehouses Typesense returns for the default view, embedded in the
         * page as a JSON <script>. Seeding straight from the DOM lets the app
         * paint real cards with no skeleton and no initial search round-trip.
         *
         * Only safe when the current filter state matches the seed (default
         * view, no deep-link params) — the caller guards that; here we just
         * load the documents and mark loading complete. Returns false when the
         * seed is absent or unparseable so the caller can fall back to fetch().
         */
        seedFromSsr(this: typeof useLocationsState): boolean {
            const element = document.getElementById("warehouse-locations-seed");

            if (!element?.textContent) {
                return false;
            }

            let hits;
            try {
                hits = JSON.parse(element.textContent);
            } catch {
                return false;
            }

            if (!Array.isArray(hits) || hits.length === 0) {
                return false;
            }

            this.locations = hits;

            this.pagination.current = 1;
            this.pagination.foundResults = hits.length;
            this.pagination.totalResults = hits.length;
            this.pagination.totalPages = this.pagination.itemsPerPage
                ? Math.ceil(hits.length / this.pagination.itemsPerPage)
                : 1;

            this.loading = false;

            return true;
        },

        async fetch(
            this: typeof useLocationsState,
            reset = false,
            setQueryParams = false,
            showLoading = true,
        ): Promise<void> {
            // Skip the skeleton when we already have cards on screen (e.g. the
            // SSR seed is painted and we're only reconciling a deep-linked
            // filter): keep the real cards visible until the fresh results swap
            // in, rather than flashing back to skeletons. Defaults to true so
            // user-initiated searches still get immediate loading feedback.
            if (showLoading) {
                this.loading = true;
            }

            // set URL query parameters if existing
            if (setQueryParams) {
                this.setQueryFilters();
            }
            this.setFiltersQuery();

            if (reset) {
                this.pagination.current = 0;
            } else {
                this.pagination.current += 1;
            }

            // BC-40: lazy-load the Typesense client (the `typesense` package is
            // ~2 MB unpacked) only when a search actually runs. The default
            // overview is seeded from the SSR payload (seedFromSsr) and never
            // calls fetch(), so Typesense stays out of the initial bundle until
            // the visitor first filters / sorts / searches.
            const { useTypesenseClient, useSearchParameters } = await import(
                "~/js/composables/typesense"
            );

            const client = useTypesenseClient()
            const searchParameters = useSearchParameters(
                this.filters,
                this.sorting
            );

            const searchRequest = {
                searches: [
                    {
                        collection: this.index,
                        facet_by:
                            "countries,statusses",
                        page: this.pagination.current,
                        per_page: this.pagination.itemsPerPage,
                        ...searchParameters
                    },
                    {
                        collection: this.index,
                        max_facet_values: 100,
                        facet_by: "",
                        filter_by: `site:${window.language}`,
                        page: 1,
                        per_page: 1,
                        q: "*",
                        sort_by: "_text_match:desc",
                    }
                ]
            }

            await client.multiSearch
                .perform(searchRequest)
                .then((results: any) => {
                    // results
                    const filteredResults = results.results[0];
                    const hits = filteredResults?.hits || [];
                    if (reset) {
                        this.locations = hits
                    } else {
                        this.locations = [
                            ...(this.locations || []),
                            ...hits,
                        ]
                    }

                    // update pagination
                    this.pagination.current = filteredResults.page
                    this.pagination.totalPages = this.pagination.itemsPerPage
                        ? Math.ceil(filteredResults.found / this.pagination.itemsPerPage)
                        : 0;
                    this.pagination.foundResults = filteredResults.found;
                    this.pagination.totalResults = filteredResults.out_of;

                    this.loading = false;
                })
        },

        setQueryFilters(this: typeof useLocationsState): void {
            if (!window.location.search) return;

            const params = new URLSearchParams(window.location.search);

            // empty values
            this.resetFilters(false, true);

            // add query string parameters to the filters
            params.forEach((value, key) => {
                if (key in this.filters) {
                    if (Array.isArray(this.filters[key].values)) {
                        const numericValue = Number(value);

                        if (isNaN(numericValue)) {
                            return
                        }

                        this.filters[key].values.push(numericValue)
                    } else {
                        this.filters[key].value = value
                    }
                }
            })
        },
        // setUrlString(this: typeof useTeambuildingState): void {
        setUrlString(this: typeof useLocationsState): void {
            const setQueryStringsFromFilters = [
                "countries",
                "statusses"
            ];

            const url = new URL(location.href);
            const params = new URLSearchParams(url.search);

            // reset query string
            setQueryStringsFromFilters.forEach((type: string) => {
                params.delete(type);
            });

            // set query string based on values
            setQueryStringsFromFilters.forEach((type: string) => {
                const filter: Filter | SingleFilter = this.filters?.[type];

                if (!filter) return;

                if ("values" in filter && filter.values.length > 0) {
                    filter.values.forEach((value: any) => {
                        params.append(type, `${value}`);
                    });
                }

                if ("value" in filter && filter.value) {
                    params.set(type, `${filter.value}`);
                }
            });

            // set show / hide filter
            params.delete("filters");
            params.append("filters", this.settings.showFilters);

            // Apply the new query string only once
            url.search = params.toString();
            history.pushState({}, "", url.toString());
        },

        resetFilters(
            // this: typeof useTeambuildingState,
            this: typeof useLocationsState,
            fetch = true,
            resetAllFilters = this.settings.showFilters
        ): void {
            let facets = [
                "countries",
                "statusses"
            ];
            const properties = [];

            if (!resetAllFilters) {
                facets = facets.filter(
                    (value: string) =>
                        value != "categories" && value != "purposes"
                );
            }

            facets.forEach((facet) => {
                this.filters[facet].values = [];
            });

            properties.forEach((property) => {
                this.filters[property].value = null;
            });

            this.filters.searchQuery = "";

            this.setFiltersQuery();

            if (fetch) {
                this.fetch(true);
            }
        },

        setFiltersQuery(this: typeof useLocationsState): void {
            this.filters.filterQuery = "";

            /* ----------------------------------------
             ** BUILD THE countries / statusses FILTERS
             ** ----------------------------------------*/
            // check if country or status has a value
            const filters = ["countries", "statusses"];
            const appliedFilters: Array<Facet> = [];
            filters.forEach((filter) => {
                if (this.filters[filter].values.length > 0) {
                    appliedFilters.push(this.filters[filter]);
                }
            });


            //console.log(appliedFilters)
            // provide a colon for grouping the buildup
            if (appliedFilters.length > 0) {
                this.filters.filterQuery += "(";
            }

            // loop through the applied filters to set the grouped categories
            for (let i = 0; i < appliedFilters.length; i++) {
                const facet = appliedFilters[i];

                this.filters.filterQuery +=
                    facet.field + ":[" + facet.values.toString() + "]";

                if (
                    appliedFilters.length > 0 &&
                    i != appliedFilters.length - 1
                ) {
                    this.filters.filterQuery += " || ";
                }
            }

            // provide a colon for grouping the buildup
            if (appliedFilters.length > 0) {
                this.filters.filterQuery += ")";
            }

            // start combination of the facets, if buildup is set
            if (this.filters.filterQuery.length > 0) {
                this.filters.filterQuery += ` && `;
            }

            /* ----------------------------------------
             ** BUILD THE FACET FILTERS
             ** ----------------------------------------*/
            const facets = [
                "countries",
                "statusses"
            ];
            const appliedFacets: Array<Facet> = [];
            facets.forEach((filter) => {
                if (this.filters[filter].values.length > 0) {
                    appliedFacets.push(this.filters[filter]);
                }
            });

            // provide a colon for grouping the buildup
            if (appliedFacets.length > 0) {
                this.filters.filterQuery += "(";
            }

            // loop through the applied filters to set the grouped categories
            for (let i = 0; i < appliedFacets.length; i++) {
                const facet = appliedFacets[i];

                this.filters.filterQuery +=
                    facet.field + ":[" + facet.values.toString() + "]";

                if (appliedFacets.length > 0 && i != appliedFacets.length - 1) {
                    this.filters.filterQuery += " && ";
                }
            }

            // provide a colon for grouping the buildup
            if (appliedFacets.length > 0) {
                this.filters.filterQuery += ")";
            }

            // start combination of the facets, if buildup is set
            if (
                this.filters.filterQuery.length > 0 &&
                appliedFacets.length > 0
            ) {
                this.filters.filterQuery += ` && `;
            }

            /* ----------------------------------------
             ** SANITISE
             ** ----------------------------------------*/
            // remove empty ' && '
            const ampersandCombination = " && ";
            if (
                this.filters.filterQuery.lastIndexOf(ampersandCombination) >=
                this.filters.filterQuery.length - 12
            ) {
                this.filters.filterQuery = this.filters.filterQuery.substring(
                    0,
                    this.filters.filterQuery.lastIndexOf(ampersandCombination)
                );
            }

            // remove empty ' || '
            const pipesCombination = " || ";
            if (
                this.filters.filterQuery.lastIndexOf(pipesCombination) >=
                this.filters.filterQuery.length - 12
            ) {
                this.filters.filterQuery = this.filters.filterQuery.substring(
                    0,
                    this.filters.filterQuery.lastIndexOf(pipesCombination)
                );
            }

            /* ----------------------------------------
             ** ADD LANGUAGE
             ** ----------------------------------------*/
            if (this.filters.filterQuery.length > 0) {
                this.filters.filterQuery += ` && `;
            }
            this.filters.filterQuery += `site:${window.language}`;

            this.setUrlString();

            //console.log("typesense call:", this.filters.filterQuery);
        },
    }
})
